/**
 * Helper functions for menu UX.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.2.1
 * @copyright  Copyright (C) 2021-2024, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *    2022-01-10 (1.1.0) (jhoward)
 *      - Add JS/CSS asset validation system to make sure all clients are on the latest version.ht (C) 2021-2022, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *    2022-03-07 (1.1.1) (jhoward)
 *      - Fix hostname used for API calls.
 *
 *    2022-08-28 (1.2.0) (jhoward)
 *      - Convert menu-functions.js to menu-functions.ts (use TypeScript).
 *      - Refactor menu reset code to forceMenuReset().
 *
 *    2023-12-18 (1.2.1) (jhoward)
 *      - Do not force reload of page in fetchMenuData() if JS/CSS assets are incorrect for admin views.
 *
 *    2024-07-09 (1.2.2) (jhoward)
 *      - Add TextualMenuComponent for type of `this` in fetchMenuData().
 *
 *    2024-07-15 (1.2.3) (jhoward)
 *      - Allow custom hostname for menu API calls in fetchMenuData().
 */

import {UpdateMenuResponse, VisualMenuComponent, TextualMenuComponent} from "./components/menu/defs";

export function fetchMenuData(this: VisualMenuComponent | TextualMenuComponent, menuId: number, contentHash = '', menuType: string, isDev = false, checkAssetVersions = true): Promise<UpdateMenuResponse | null> {
  if (-1 === ['TEXTUAL', 'FLIPBOOK'].indexOf(menuType)) {
    return new Promise((resolve, reject) => {
      reject(null);
    });
  } else {
    const hostname = ('' === this.apiHostname) ? location.hostname : this.apiHostname;
    return fetch('https://' + hostname + '/library/api/menu.php?_=' + (new Date().getTime()), {
      method: 'POST',
      cache: 'no-cache',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      redirect: 'follow',
      body: 'ACTION=GET_' + menuType + '_MENU&MENU_ID=' + menuId + '&CONTENT_HASH=' + encodeURIComponent(contentHash) + ((isDev) ? '&test=1' : '')
    })
      .then(response => {
        return response.json();
      })
      .then(json => {
        //validate assets before passing data on
        if (json.DATA.CURRENT_ASSETS && checkAssetVersions && !this.isAdminView) {
          let assetMissing = false;
          for (let i = 0, l = json.DATA.CURRENT_ASSETS.length; i < l; i++) {
            if ('.js' === json.DATA.CURRENT_ASSETS[i].substr(-3, 3) && !document.querySelector('script[src="' + json.DATA.CURRENT_ASSETS[i] + '"]')) {
              assetMissing = true; //JS asset not found
              break;
            } else if ('.css' === json.DATA.CURRENT_ASSETS[i].substr(-4, 4) && !document.querySelector('link[href="' + json.DATA.CURRENT_ASSETS[i] + '"]')) {
              assetMissing = true; //CSS asset not found
              break;
            }
          }

          if (assetMissing) {
            //if a cache-busing parameter already exists, we don't want to keep making more
            forceMenuReset();
          }
        }
        return json;
      })
      .catch(e => {
        console.log(e);
      });
  }
}

export function forceMenuReset(): void {
  if (/[?&]_=\d+($|&)/.test(location.href)) {
    location.href = location.href.replace(/([?&]_)=\d+($|&)/, '$1=' + new Date().getTime() + '$2');
  } else {
    //no cache-busting parameter exists, we can safely create a new one
    location.href = location.href + ((-1 === location.href.indexOf('?')) ? '?' : '&') + '_=' + new Date().getTime();
  }
}